import { useRef, useCallback, useMemo } from 'react';

import { Pencil, Trash2 } from 'lucide-react';
import { useDrag, useDrop } from 'react-dnd';

import { formatTitle } from '../../utils/common';

import { SubMenuItem } from './SubMenuItem';

import type { DragItem, IAllMenu } from './model';

const ItemTypes = {
  MENU_ITEM: 'menuItem',
  SUB_MENU_ITEM: 'subMenuItem',
};

const DragHandleIcon = () => (
  <svg
    className="w-4 h-4 text-gray-400"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <circle cx="8" cy="6" r="1.5" />
    <circle cx="8" cy="12" r="1.5" />
    <circle cx="8" cy="18" r="1.5" />
    <circle cx="16" cy="6" r="1.5" />
    <circle cx="16" cy="12" r="1.5" />
    <circle cx="16" cy="18" r="1.5" />
  </svg>
);

export const MenuItemComponent = ({
  item,
  index,
  onDrop,
  onEdit,
  onDelete,
  onSubMenuDrop,
}: {
  item: IAllMenu;
  index: number;
  onDrop: (dragIndex: number, hoverIndex: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number, name: string) => void;
  onSubMenuDrop: (
    parentId: number,
    dragIndex: number,
    hoverIndex: number
  ) => void;
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.MENU_ITEM,
    item: { index, type: ItemTypes.MENU_ITEM, id: item.adminMenuId },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: ItemTypes.MENU_ITEM,
    drop: (draggedItem: DragItem) => {
      if (draggedItem.index !== index) {
        onDrop(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  const ref = useRef<HTMLDivElement>(null);
  drag(drop(ref));

  const handleDragStart = useCallback(
    (e: React.DragEvent) => e.stopPropagation(),
    []
  );

  const hasSubMenus = (item.subMenu ?? []).length > 0;
  const isDraggingParent = isDragging && hasSubMenus;

  const wrapperClass = useMemo(
    () => `mb-2 ${isDraggingParent ? 'z-50' : ''}`,
    [isDraggingParent]
  );

  const containerClass = useMemo(() => {
    let classes = `
      px-3 py-2 flex items-center justify-between select-none
      border border-gray-300 rounded-t bg-white
      shadow transition-all duration-200 cursor-move
    `;
    if (isDraggingParent)
      classes += ' shadow-2xl shadow-gray-700 border-2 z-50';
    if (isDragging && !hasSubMenus) classes += ' opacity-40';
    if (isOver) classes += ' border-2 border-dashed border-gray-400 bg-gray-50';
    return classes;
  }, [isDraggingParent, isDragging, hasSubMenus, isOver]);

  return (
    <div
      className={wrapperClass}
      style={{ position: isDraggingParent ? 'relative' : undefined }}
    >
      <div
        ref={ref}
        className={containerClass}
        style={{
          borderBottomLeftRadius: hasSubMenus ? 0 : undefined,
          borderBottomRightRadius: hasSubMenus ? 0 : undefined,
        }}
      >
        <div className="flex items-center">
          <button
            type="button"
            className="mr-2 cursor-move"
            onDragStart={handleDragStart}
            draggable
          >
            <DragHandleIcon />
          </button>
          <span className="text-gray-800">{formatTitle(item.menuDisplay)}</span>
        </div>

        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => onEdit(item.adminMenuId)}
            className="w-7 h-7 flex items-center justify-center rounded
              border border-brand-500 text-brand-500
              hover:bg-brand-500 hover:text-white transition cursor-pointer"
          >
            <Pencil size={12} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(item.adminMenuId, item.menuDisplay)}
            className="w-7 h-7 flex items-center justify-center rounded
              border border-error-400 text-error-500
              hover:bg-red-500 hover:text-white transition cursor-pointer"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {hasSubMenus && (
        <div
          className={`
            ml-6 mt-1 space-y-1
            ${
              isDraggingParent
                ? 'bg-white border-x border-b border-gray-300 rounded-b shadow-2xl shadow-gray-700 transition-all duration-200 z-50'
                : ''
            }
          `}
          style={{
            marginTop: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          {item.subMenu?.map((subItem, subIndex) => (
            <SubMenuItem
              key={subItem.adminMenuId}
              item={subItem}
              index={subIndex}
              parentId={item.adminMenuId}
              onDrop={onSubMenuDrop}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};
